import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import CustomSpinner from '../CustomSpinner/CustomSpinner';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { message } from 'antd';
import { Howl } from 'howler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faChair,
  faTimes,
  faChartBar,
  faFilter,
  faBell,
  faStickyNote,
  faTags,
  faCheckSquare,
  faFileInvoiceDollar,
  faClock,
  faHistory,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const deleteTables = new Howl({ src: ['/sounds/delete.mp3'] });
const canceleButton = new Howl({ src: ['/sounds/cancel.mp3'] });
const addTableSound = new Howl({ src: ['/sounds/submit.mp3'] });
const clickDelete = new Howl({ src: ['/sounds/click.mp3'] });

// Tag colors for display
const TAG_COLORS = {
  'Indoor': '#3498db',
  'Outdoor': '#27ae60',
  'Private': '#9b59b6',
  'Bar': '#e67e22',
  'Window': '#1abc9c',
  'VIP': '#f1c40f',
  'Quiet Zone': '#95a5a6'
};

const AVAILABLE_TAGS = ['Indoor', 'Outdoor', 'Private', 'Bar', 'Window', 'VIP', 'Quiet Zone'];

function SlotTable(props) {
  const { slotNumber } = useParams();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [tableNumber, setTableNumber] = useState('');
  const [tableCapacity, setTableCapacity] = useState('');
  const [loadingTable, setLoadingTable] = useState(null);
  const [addingTable, setAddingTable] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [tableToChange, setTableToChange] = useState(null);
  const [availableTables, setAvailableTables] = useState([]);
  const [selectedNewTable, setSelectedNewTable] = useState('');
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [tableToReserve, setTableToReserve] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const socket = useSocket();

  // Phase 28: New State Variables
  const [stats, setStats] = useState({ total: 0, reserved: 0, available: 0, disabled: 0, capacityBreakdown: {} });
  const [capacityFilter, setCapacityFilter] = useState('all');
  const [selectedTables, setSelectedTables] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [tableToEdit, setTableToEdit] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [tick, setTick] = useState(0); // Force re-render for timer

  const fetchTables = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) {
        setTables([]);
        return;
      }

      const response = await axios.get(buildApiUrl(`slot/${slotNumber}?restaurantId=${activeRestaurantId}`));
      setTables(response.data);
    } catch (error) {
      console.error('Error fetching tables:', error);
      message.error('Error fetching tables');
    }
  }, [slotNumber]);

  const handleSlotUpdate = useCallback((data) => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;

    // Handle populated restaurantId object
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    // Filter: Only process events for our restaurant
    if (data.restaurantId && data.restaurantId !== activeRestaurantId) return;

    if (data.slotNumber.toString() === slotNumber) {
      setTables(prevTables => {
        if (data.slot) {
          return prevTables.map(table => 
            table._id === data.slot._id ? data.slot : table
          );
        }
        
        return prevTables.map(table => {
          if (table.number === data.tableNumber) {
            return {
              ...table,
              reserved: data.action === 'reserved',
              reservedBy: data.reservedBy || null,
              disabled: data.action === 'tableDisabled' ? true : 
                       data.action === 'tableEnabled' ? false : table.disabled
            };
          }
          return table;
        });
      });
    }
  }, [slotNumber]);

  const handleTableAdded = useCallback((data) => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;

    // Handle populated restaurantId object
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    // Filter: Only process events for our restaurant
    if (data.restaurantId && data.restaurantId !== activeRestaurantId) return;

    if (data.slotNumber.toString() === slotNumber) {
      setTables(prevTables => [...prevTables, data.table].sort((a, b) => a.number - b.number));
    }
  }, [slotNumber]);
  
  const handleTableDeleted = useCallback((data) => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;

    // Handle populated restaurantId object
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    // Filter: Only process events for our restaurant
    if (data.restaurantId && data.restaurantId !== activeRestaurantId) return;

    if (data.slotNumber.toString() === slotNumber) {
      setTables(prevTables => prevTables.filter(table => table.number !== data.tableNumber));
    }
  }, [slotNumber]);

  const handleTableChanged = useCallback((data) => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;

    // Handle populated restaurantId object
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    // Filter: Only process events for our restaurant
    if (data.restaurantId && data.restaurantId !== activeRestaurantId) return;

    if (data.slotNumber.toString() === slotNumber) {
      setTables(prevTables => prevTables.map(table => {
        if (table.number === data.oldTableNumber) {
          return { ...table, reserved: false, reservedBy: null };
        }
        if (table.number === data.newTableNumber) {
          return { ...table, reserved: true, reservedBy: data.reservedBy };
        }
        return table;
      }));
    }
  }, [slotNumber]);

  // Phase 28: Fetch Stats
  const fetchStats = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) return;

      const response = await axios.get(buildApiUrl(`slot/${slotNumber}/stats?restaurantId=${activeRestaurantId}`));
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [slotNumber]);

  useEffect(() => {
    fetchTables();
    fetchStats();
    
    // Get restaurant ID for scoped socket room
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }
    const socketRoom = activeRestaurantId ? `slot_${slotNumber}_${activeRestaurantId}` : `slot_${slotNumber}`;
    
    if (socket) {
      socket.emit('joinRoom', socketRoom);
      socket.on('slotUpdated', handleSlotUpdate);
      socket.on('tableAdded', handleTableAdded);
      socket.on('tableDeleted', handleTableDeleted);
      socket.on('tableChanged', handleTableChanged);
      socket.on('bulkTableUpdate', (data) => { 
        // Filter bulk updates by restaurant too
        if (data?.restaurantId && data.restaurantId !== activeRestaurantId) return;
        fetchTables(); 
        fetchStats(); 
      });
      socket.on('tableTagsUpdated', ({ tableNumber, tags, restaurantId }) => {
        if (restaurantId && restaurantId !== activeRestaurantId) return;
        setTables(prev => prev.map(t => t.number === tableNumber ? { ...t, tags } : t));
      });
      socket.on('tableNotesUpdated', ({ tableNumber, notes, restaurantId }) => {
        if (restaurantId && restaurantId !== activeRestaurantId) return;
        setTables(prev => prev.map(t => t.number === tableNumber ? { ...t, notes } : t));
      });
      
      return () => {
        socket.off('slotUpdated', handleSlotUpdate);
        socket.off('tableAdded', handleTableAdded);
        socket.off('tableDeleted', handleTableDeleted);
        socket.off('tableChanged', handleTableChanged);
        socket.off('bulkTableUpdate');
        socket.off('tableTagsUpdated');
        socket.off('tableNotesUpdated');
        socket.emit('leaveRoom', `slot_${slotNumber}`);
      };
    }
  }, [socket, slotNumber, fetchTables, fetchStats, handleSlotUpdate, handleTableAdded, handleTableDeleted, handleTableChanged]);

  // Phase 28: Logic to tick the timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 1000); // 1 second
    return () => clearInterval(timer);
  }, []);

  // Phase 28: Timer utility - format elapsed time
  // Phase 28: Timer utility - format elapsed time (m:s or h:m:s)
  const getElapsedTime = (reserveDate) => {
    if (!reserveDate) return null;
    const now = new Date();
    const reserved = new Date(reserveDate);
    const diffInSeconds = Math.floor((now - reserved) / 1000);
    
    // Safeguard level
    if (diffInSeconds > 86400 || diffInSeconds < 0) return "Just Seat"; 

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    const pad = (num) => num.toString().padStart(2, '0');

    if (hours > 0) {
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    return `${minutes}m ${pad(seconds)}s`;
  };

  // Phase 28: Update Notes
  const saveNotes = async () => {
    if (!tableToEdit) return;
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      await axios.patch(buildApiUrl(`slot/${slotNumber}/notes`), 
        { number: tableToEdit.number, notes: editNotes, restaurantId: activeRestaurantId },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success('Notes saved');
      setShowNotesModal(false);
      fetchTables();
    } catch (error) {
      message.error(error.response?.data?.message || 'Error saving notes');
    }
  };

  // Phase 28: Update Tags
  const saveTags = async () => {
    if (!tableToEdit) return;
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      await axios.patch(buildApiUrl(`slot/${slotNumber}/tags`), 
        { number: tableToEdit.number, tags: editTags, restaurantId: activeRestaurantId },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success('Tags saved');
      setShowTagsModal(false);
      // Immediately update local state for better UX
      setTables(prev => prev.map(t => t.number === tableToEdit.number ? { ...t, tags: editTags } : t));
      fetchTables();
    } catch (error) {
      message.error(error.response?.data?.message || 'Error saving tags');
    }
  };

  // Phase 28: Notify Guest
  const notifyGuest = async (tableNum) => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      const response = await axios.post(buildApiUrl(`slot/${slotNumber}/notify-guest`), 
        { number: tableNum, restaurantId: activeRestaurantId },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success(response.data.message);
    } catch (error) {
      message.error(error.response?.data?.message || 'Error sending notification');
    }
  };

  // Phase 28: Bulk Actions
  const executeBulkAction = async (action) => {
    if (selectedTables.length === 0) {
      message.warning('No tables selected');
      return;
    }
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      await axios.post(buildApiUrl(`slot/${slotNumber}/bulk-action`), 
        { tableNumbers: selectedTables, action, restaurantId: activeRestaurantId },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success(`Bulk ${action} completed`);
      setSelectedTables([]);
      setBulkMode(false);
      fetchTables();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || 'Bulk action failed');
    }
  };

  // Phase 28: Toggle table selection
  const toggleTableSelection = (tableNum) => {
    setSelectedTables(prev => 
      prev.includes(tableNum) 
        ? prev.filter(n => n !== tableNum) 
        : [...prev, tableNum]
    );
  };

  const addTable = async () => {
    if (!tableNumber || !tableCapacity) {
      message.error('Table number and capacity required');
      return;
    }

    try {
      setAddingTable(true);
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      await axios.post(buildApiUrl(`slot/${slotNumber}/add`), {
        number: tableNumber,
        capacity: tableCapacity,
        restaurantId: activeRestaurantId
      });
      message.success('Table added');
      addTableSound.play();
      fetchTables();
      setTableNumber('');
      setTableCapacity('');
    } catch (error) {
      console.error('Error adding table:', error);
      message.error(error.response?.data?.message || 'Error adding table');
    } finally {
      setAddingTable(false);
    }
  };

  const confirmDelete = (number) => {
    setTableToDelete(number);
    setShowDeleteModal(true);
  };

  const deleteTable = async () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      await axios.delete(buildApiUrl(`slot/${slotNumber}/delete`), { 
        data: { number: tableToDelete, restaurantId: activeRestaurantId }
      });
      deleteTables.play();
      message.success('Table deleted successfully');
      fetchTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      message.error('Error deleting table');
    } finally {
      setShowDeleteModal(false);
      setTableToDelete(null);
    }
  };

  const unreserveTable = async (number) => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      setLoadingTable(number);
      await axios.post(
        buildApiUrl(`slot/${slotNumber}/admin/unreserve`), 
        { number, restaurantId: activeRestaurantId }, 
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success('Table unreserved');
      await fetchTables();
    } catch (error) {
      console.error('Error unreserving table:', error);
      message.error('Error unreserving table');
    } finally {
      setLoadingTable(null);
    }
  };

  const toggleTableStatus = async (number) => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      setLoadingTable(number);
      await axios.post(
        buildApiUrl(`slot/${slotNumber}/toggle-status`),
        { number, restaurantId: activeRestaurantId },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      await fetchTables();
    } catch (error) {
      console.error('Error toggling table status:', error);
      message.error('Error updating table status');
    } finally {
      setLoadingTable(null);
    }
  };

  const openChangeModal = async (table) => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      setTableToChange(table);
      const response = await axios.get(
        buildApiUrl(`slot/${slotNumber}/available-tables`),
        { params: { capacity: table.capacity, exclude: table.number, restaurantId: activeRestaurantId } }
      );
      setAvailableTables(response.data);
      setShowChangeModal(true);
    } catch (error) {
      console.error('Error fetching available tables:', error);
      message.error('Error fetching available tables');
    }
  };

  const changeTable = async () => {
    if (!selectedNewTable) {
      message.error('Please select a new table');
      return;
    }

    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      setLoadingTable(tableToChange.number);
      await axios.post(
        buildApiUrl(`slot/${slotNumber}/change-table`),
        { 
          oldTableNumber: tableToChange.number,
          newTableNumber: selectedNewTable,
          restaurantId: activeRestaurantId
        },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success('Table changed successfully');
      setShowChangeModal(false);
      setSelectedNewTable('');
      fetchTables();
    } catch (error) {
      console.error('Error changing table:', error);
      message.error(error.response?.data?.message || 'Error changing table');
    } finally {
      setLoadingTable(null);
    }
  };

  const openReserveModal = async (table) => {
    const uDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      setTableToReserve(table);
      const response = await axios.get(buildApiUrl(`users/admin/all-users?restaurantId=${activeRestaurantId}`), {
        headers: { 'auth-token': localStorage.getItem('token') }
      });
      setUsers(response.data);
      setShowReserveModal(true);
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Error fetching users');
    }
  };

  const reserveForUser = async () => {
    if (!selectedUserId) {
      message.error('Please select a user');
      return;
    }

    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    try {
      setLoadingTable(tableToReserve.number);
      await axios.post(
        buildApiUrl(`slot/${slotNumber}/admin/reserve`),
        { 
          number: tableToReserve.number,
          userId: selectedUserId,
          restaurantId: activeRestaurantId
        },
        { headers: { 'auth-token': localStorage.getItem('token') } }
      );
      message.success('Table reserved successfully for user');
      setShowReserveModal(false);
      setSelectedUserId('');
      setSearchTerm('');
      fetchTables();
    } catch (error) {
      console.error('Error reserving table:', error);
      message.error(error.response?.data?.message || 'Error reserving table');
    } finally {
      setLoadingTable(null);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.contact.includes(searchTerm)
  );

  const sortedTables = [...tables].sort((a, b) => a.number - b.number);

  return (
    <div className="admin-layout">
      <Sidebar />
      <main className="tf-container">
        <header className="tf-page-header">
           <div className="tf-header-content">
              <div className="tf-header-title-group">
                 <h1 className="tf-page-title">Manage Tables <span className="tf-text-accent" style={{ fontSize: '0.8em', opacity: 0.7 }}>Slot {slotNumber}</span></h1>
              </div>
              <div className="tf-flex" style={{ gap: '1rem' }}>
                 <button 
                   className={`tf-btn ${bulkMode ? 'tf-btn-primary' : ''}`} 
                   style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', background: bulkMode ? '' : 'rgba(255,255,255,0.1)' }}
                   onClick={() => { setBulkMode(!bulkMode); setSelectedTables([]); }}
                 >
                   <FontAwesomeIcon icon={faCheckSquare} /> {bulkMode ? 'Exit Bulk' : 'Bulk Mode'}
                 </button>
              </div>
           </div>
        </header>

        {/* Phase 28: Statistics Dashboard */}
        <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
           <div className="tf-card" style={{ padding: '1rem', textAlign: 'center', borderLeft: '4px solid var(--tf-primary)' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700' }}>{stats.total}</div>
              <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>Total Tables</div>
           </div>
           <div className="tf-card" style={{ padding: '1rem', textAlign: 'center', borderLeft: '4px solid var(--tf-primary)' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--tf-primary)' }}>{stats.reserved}</div>
              <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>Reserved</div>
           </div>
           <div className="tf-card" style={{ padding: '1rem', textAlign: 'center', borderLeft: '4px solid var(--tf-success)' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--tf-success)' }}>{stats.available}</div>
              <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>Available</div>
           </div>
           <div className="tf-card" style={{ padding: '1rem', textAlign: 'center', borderLeft: '4px solid var(--tf-text-muted)' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--tf-text-muted)' }}>{stats.disabled}</div>
              <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>Disabled</div>
           </div>
        </div>

        {/* Phase 28: Filters & Bulk Actions Bar */}
        <div className="tf-card" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
           <div className="tf-flex" style={{ gap: '1rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div className="tf-flex" style={{ gap: '1rem' }}>
                 <div className="tf-flex" style={{ gap: '0.5rem', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faFilter} style={{ color: 'var(--tf-text-muted)' }} />
                    <select 
                      className="tf-input" 
                      style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                      value={capacityFilter}
                      onChange={(e) => setCapacityFilter(e.target.value)}
                    >
                       <option value="all">All Capacities</option>
                       {Object.keys(stats.capacityBreakdown).sort((a,b) => a - b).map(cap => (
                          <option key={cap} value={cap}>{cap} Seater ({stats.capacityBreakdown[cap]?.available || 0} avail)</option>
                       ))}
                    </select>
                 </div>
              </div>
              
              {bulkMode && (
                 <div className="tf-flex" style={{ gap: '0.75rem' }}>
                    <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>{selectedTables.length} selected</span>
                    <button className="tf-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', background: 'rgba(0, 209, 178, 0.2)', color: 'var(--tf-success)' }} onClick={() => executeBulkAction('enable')}>Enable</button>
                    <button className="tf-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', background: 'rgba(255,255,255,0.1)' }} onClick={() => executeBulkAction('disable')}>Disable</button>
                    <button className="tf-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', background: 'rgba(255, 65, 53, 0.2)', color: 'var(--tf-error)' }} onClick={() => executeBulkAction('unreserve')}>Unreserve</button>
                 </div>
              )}
           </div>
        </div>

        {/* Phase 28: Notes Modal */}
        {showNotesModal && (
           <div className="tf-modal-overlay">
              <div className="tf-modal" style={{ maxWidth: '500px' }}>
                 <div className="tf-modal-body">
                    <h3 className="tf-heading-sm" style={{ marginBottom: '1.5rem' }}><FontAwesomeIcon icon={faStickyNote} /> Notes for Table {tableToEdit?.number}</h3>
                    <textarea 
                       className="tf-input" 
                       style={{ minHeight: '150px', resize: 'vertical' }} 
                       placeholder="Add notes (e.g., VIP guest, special requests...)"
                       value={editNotes}
                       onChange={(e) => setEditNotes(e.target.value)}
                       maxLength={500}
                    />
                    <div style={{ textAlign: 'right', color: 'var(--tf-text-muted)', fontSize: '0.8rem', marginTop: '0.5rem' }}>{editNotes.length}/500</div>
                 </div>
                 <div className="tf-modal-footer">
                    <button className="tf-btn tf-btn-outline" onClick={() => setShowNotesModal(false)}>Cancel</button>
                    <button className="tf-btn tf-btn-primary" onClick={saveNotes}>Save Notes</button>
                 </div>
              </div>
           </div>
        )}

        {/* Phase 28: Tags Modal */}
        {showTagsModal && (
           <div className="tf-modal-overlay">
              <div className="tf-modal" style={{ maxWidth: '500px' }}>
                 <div className="tf-modal-body">
                    <h3 className="tf-heading-sm" style={{ marginBottom: '1.5rem' }}><FontAwesomeIcon icon={faTags} /> Tags for Table {tableToEdit?.number}</h3>
                    <div className="tf-flex" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                       {AVAILABLE_TAGS.map(tag => (
                          <button 
                             key={tag}
                             className="tf-btn"
                             style={{ 
                                padding: '0.4rem 0.8rem', 
                                fontSize: '0.8rem',
                                background: editTags.includes(tag) ? TAG_COLORS[tag] : 'rgba(255,255,255,0.1)',
                                color: editTags.includes(tag) ? '#fff' : 'var(--tf-text-muted)',
                                border: editTags.includes(tag) ? 'none' : '1px solid var(--tf-border)'
                             }}
                             onClick={() => setEditTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                          >
                             {tag}
                          </button>
                       ))}
                    </div>
                 </div>
                 <div className="tf-modal-footer">
                    <button className="tf-btn tf-btn-outline" onClick={() => setShowTagsModal(false)}>Cancel</button>
                    <button className="tf-btn tf-btn-primary" onClick={saveTags}>Save Tags</button>
                 </div>
              </div>
           </div>
        )}

        {showDeleteModal && (
          <div className="tf-modal-overlay">
            <div className="tf-modal" style={{ maxWidth: '450px' }}>
              <div className="tf-modal-body" style={{ textAlign: 'center', paddingTop: '3rem' }}>
                <h3 className="tf-heading-sm" style={{ marginBottom: '1rem' }}>Confirm Deletion</h3>
                <p style={{ marginBottom: '2rem', color: 'var(--tf-text-muted)' }}>Are you sure you want to delete Table {tableToDelete}? This action cannot be undone.</p>
                <div className="tf-flex-center" style={{ gap: '1rem' }}>
                  <button className="tf-btn tf-btn-outline" onClick={() => { setShowDeleteModal(false); canceleButton.play(); }}>Cancel</button>
                  <button className="tf-btn tf-btn-primary" style={{ backgroundColor: 'var(--tf-error)', borderColor: 'var(--tf-error)' }} onClick={deleteTable}>Delete Table</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showChangeModal && (
          <div className="tf-modal-overlay">
            <div className="tf-modal" style={{ maxWidth: '500px' }}>
              <div className="tf-modal-body">
                <h3 className="tf-heading-sm" style={{ marginBottom: '1.5rem' }}>Change Table {tableToChange?.number}</h3>
                <div className="tf-grid" style={{ gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>New Table Selection</label>
                    <select className="tf-input" value={selectedNewTable} onChange={(e) => setSelectedNewTable(e.target.value)}>
                      <option value="">Select a table ({tableToChange?.capacity} seats)</option>
                      {availableTables.map(table => (
                        <option key={table.number} value={table.number}>Table {table.number}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="tf-modal-footer">
                  <button className="tf-btn tf-btn-outline" style={{ marginRight: '1rem' }} onClick={() => { setShowChangeModal(false); setSelectedNewTable(''); }}>Cancel</button>
                  <button className="tf-btn tf-btn-primary" onClick={changeTable} disabled={!selectedNewTable || loadingTable === tableToChange?.number}>
                    {loadingTable === tableToChange?.number ? 'Changing...' : 'Confirm Change'}
                  </button>
              </div>
            </div>
          </div>
        )}

        {showReserveModal && (
          <div className="tf-modal-overlay">
            <div className="tf-modal" style={{ maxWidth: '600px' }}>
              <div className="tf-modal-body" style={{ padding: '0' }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--tf-border)' }}>
                   <h3 className="tf-heading-sm">Reserve Table {tableToReserve?.number}</h3>
                </div>
                
                <div style={{ padding: '1.5rem' }}>
                   <div className="tf-search-container" style={{ maxWidth: '100%', marginBottom: '1.5rem' }}>
                      <FontAwesomeIcon icon={faSearch} className="tf-search-icon" />
                      <input 
                        type="text" 
                        placeholder="Search users by name or contact..." 
                        className="tf-search-input" 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                      />
                   </div>

                   <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--tf-border)', borderRadius: 'var(--tf-radius-md)' }}>
                     {filteredUsers.length > 0 ? (
                       filteredUsers.map(user => (
                         <div 
                           key={user._id} 
                           onClick={() => setSelectedUserId(user._id)} 
                           className="tf-list-item-row"
                           style={{ 
                             cursor: 'pointer', 
                             background: selectedUserId === user._id ? 'rgba(255, 65, 53, 0.1)' : 'transparent',
                             borderColor: selectedUserId === user._id ? 'var(--tf-primary)' : 'transparent',
                             gridTemplateColumns: '1fr',
                             padding: '1rem'
                           }}
                         >
                           <div className="tf-flex" style={{ gap: '1rem' }}>
                              <div className="tf-avatar tf-avatar-sm">{user.name.charAt(0)}</div>
                              <div className="tf-user-info">
                                 <h4>{user.name}</h4>
                                 <p>{user.email} | {user.contact}</p>
                              </div>
                           </div>
                         </div>
                       ))
                     ) : (
                       <div className="tf-empty-state" style={{ padding: '2rem' }}>No users found</div>
                     )}
                   </div>
                </div>
              </div>
              <div className="tf-modal-footer">
                  <button className="tf-btn tf-btn-outline" style={{ marginRight: '1rem' }} onClick={() => { setShowReserveModal(false); setSelectedUserId(''); setSearchTerm(''); }}>Cancel</button>
                  <button className="tf-btn tf-btn-primary" onClick={reserveForUser} disabled={!selectedUserId || loadingTable === tableToReserve?.number}>
                    {loadingTable === tableToReserve?.number ? 'Reserving...' : 'Confirm Reservation'}
                  </button>
              </div>
            </div>
          </div>
        )}

        <div className="tf-card" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
          <div className="tf-flex" style={{ gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
               <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>New Table Number</label>
               <input type="number" className="tf-input" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="e.g. 15" />
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
               <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>Seating Capacity</label>
               <input type="number" className="tf-input" value={tableCapacity} onChange={(e) => setTableCapacity(e.target.value)} placeholder="e.g. 4" />
            </div>
            <button className="tf-btn tf-btn-primary" onClick={addTable} disabled={addingTable} style={{ flex: '0 0 150px', height: '46px' }}>
              {addingTable ? 'Adding...' : 'Add Table'}
            </button>
          </div>
        </div>

        <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {sortedTables
            .filter(t => capacityFilter === 'all' || t.capacity === parseInt(capacityFilter))
            .map(table => (
            <div key={table._id} className="tf-card" style={{ 
               borderLeft: table.reserved ? '4px solid var(--tf-primary)' : table.disabled ? '4px solid var(--tf-text-muted)' : '4px solid var(--tf-success)', 
               display: 'flex', 
               flexDirection: 'column', 
               gap: '0.75rem',
               padding: '1.25rem',
               background: selectedTables.includes(table.number) ? 'rgba(255, 65, 53, 0.05)' : ''
            }}>
              {/* Header Row with Bulk Checkbox */}
              <div className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div className="tf-flex" style={{ gap: '0.75rem', alignItems: 'center' }}>
                   {bulkMode && (
                      <input 
                        type="checkbox" 
                        checked={selectedTables.includes(table.number)}
                        onChange={() => toggleTableSelection(table.number)}
                        style={{ width: '18px', height: '18px', accentColor: 'var(--tf-primary)' }}
                      />
                   )}
                   <div>
                     <h3 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '0.1rem' }}>Table {table.number}</h3>
                     <div className="tf-flex" style={{ gap: '0.5rem', color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>
                        <FontAwesomeIcon icon={faChair} />
                        <span>{table.capacity} Seats</span>
                     </div>
                   </div>
                </div>
                <div className="tf-flex" style={{ gap: '0.5rem' }}>
                   {/* Timer for reserved tables */}
                   {table.reserved && table.reserveDate && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--tf-primary)', background: 'rgba(255, 65, 53, 0.1)', padding: '0.25rem 0.5rem', borderRadius: '12px' }}>
                         <FontAwesomeIcon icon={faClock} /> {getElapsedTime(table.reserveDate)}
                      </span>
                   )}
                   <button
                     onClick={() => toggleTableStatus(table.number)}
                     className="tf-btn"
                     style={{ 
                        padding: '0.3rem 0.6rem', 
                        fontSize: '0.7rem', 
                        background: table.disabled ? 'rgba(255,255,255,0.1)' : 'rgba(0, 209, 178, 0.1)', 
                        color: table.disabled ? 'var(--tf-text-muted)' : 'var(--tf-success)',
                        borderRadius: '15px'
                     }}
                   >
                     {table.disabled ? 'Disabled' : 'Active'}
                   </button>
                </div>
              </div>

              {/* Tags Row */}
              {table.tags && table.tags.length > 0 && (
                 <div className="tf-flex" style={{ gap: '0.4rem', flexWrap: 'wrap', margin: '0.25rem 0' }}>
                    {table.tags.map(tag => (
                       <span key={tag} style={{ 
                          fontSize: '0.75rem', 
                          padding: '0.2rem 0.6rem', 
                          borderRadius: '6px', 
                          background: TAG_COLORS[tag] || 'var(--tf-bg-input)', 
                          color: '#fff',
                          fontWeight: '600',
                          border: '1px solid rgba(255,255,255,0.1)',
                          display: 'inline-flex',
                          alignItems: 'center'
                       }}>{tag}</span>
                    ))}
                 </div>
              )}

              {/* Reserved Info or Available Status */}
              {table.reserved ? (
                <div style={{ padding: '0.75rem', background: 'rgba(255, 65, 53, 0.05)', borderRadius: 'var(--tf-radius-md)', border: '1px solid rgba(255, 65, 53, 0.1)' }}>
                   <div className="tf-flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="tf-user-info">
                         <p style={{ color: 'var(--tf-primary)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.65rem', marginBottom: '0.25rem' }}>Reserved By</p>
                         <h4 style={{ fontSize: '0.95rem' }}>{table.reservedBy?.name || 'Loading...'}</h4>
                         <p style={{ fontSize: '0.8rem' }}>{table.reservedBy?.contact || ''}</p>
                      </div>
                      <button 
                        onClick={() => notifyGuest(table.number)}
                        className="tf-btn" 
                        style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem', background: 'rgba(0, 209, 178, 0.15)', color: 'var(--tf-success)' }}
                        title="Send Table Ready notification"
                      >
                         <FontAwesomeIcon icon={faBell} />
                      </button>
                   </div>
                </div>
              ) : (
                 <div style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--tf-radius-md)', border: '1px dashed var(--tf-border)', textAlign: 'center', color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>
                    Available for reservation
                 </div>
              )}

              {/* Notes Indicator */}
              {table.notes && (
                 <div style={{ fontSize: '0.8rem', color: 'var(--tf-text-muted)', fontStyle: 'italic', padding: '0.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>
                    <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: '0.4rem' }} />
                    {table.notes.length > 60 ? table.notes.substring(0, 60) + '...' : table.notes}
                 </div>
              )}

              {/* Action Buttons */}
              <div className="tf-flex" style={{ marginTop: 'auto', gap: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--tf-border)', flexWrap: 'wrap' }}>
                 {!table.reserved && !table.disabled && (
                   <button onClick={() => openReserveModal(table)} className="tf-btn tf-btn-primary" style={{ flex: 1, padding: '0.5rem', fontSize: '0.8rem' }}>Reserve</button>
                 )}
                 {table.reserved && (
                   <>
                     <button onClick={() => unreserveTable(table.number)} className="tf-btn tf-btn-outline" style={{ flex: 1, padding: '0.5rem', fontSize: '0.8rem' }}>Unreserve</button>
                     <button onClick={() => openChangeModal(table)} className="tf-btn" style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.1)', fontSize: '0.8rem' }} title="Change Table">Shift</button>
                     {/* Quick Bill Button */}
                     <button 
                       onClick={() => navigate(`/admin/create-bill?userId=${table.reservedBy?._id}`)}
                       className="tf-btn" 
                       style={{ padding: '0.5rem', background: 'rgba(0, 209, 178, 0.15)', color: 'var(--tf-success)', fontSize: '0.8rem' }} 
                       title="Create Bill"
                       disabled={!table.reservedBy?._id}
                     >
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                     </button>
                   </>
                 )}
                 {/* Notes & Tags Buttons */}
                 <button 
                   onClick={() => { setTableToEdit(table); setEditNotes(table.notes || ''); setShowNotesModal(true); }}
                   className="tf-btn" 
                   style={{ padding: '0.5rem', background: table.notes ? 'rgba(241, 196, 15, 0.15)' : 'rgba(255,255,255,0.05)', color: table.notes ? '#f1c40f' : 'var(--tf-text-muted)', fontSize: '0.8rem' }}
                   title="Notes"
                 >
                    <FontAwesomeIcon icon={faStickyNote} />
                 </button>
                 <button 
                   onClick={() => { setTableToEdit(table); setEditTags(table.tags || []); setShowTagsModal(true); }}
                   className="tf-btn" 
                   style={{ padding: '0.5rem', background: (table.tags?.length > 0) ? 'rgba(155, 89, 182, 0.15)' : 'rgba(255,255,255,0.05)', color: (table.tags?.length > 0) ? '#9b59b6' : 'var(--tf-text-muted)', fontSize: '0.8rem' }}
                   title="Tags"
                 >
                    <FontAwesomeIcon icon={faTags} />
                 </button>
                 <button onClick={() => confirmDelete(table.number)} className="tf-btn" style={{ padding: '0.5rem', color: 'var(--tf-error)', background: 'rgba(255, 65, 53, 0.1)', fontSize: '0.8rem' }} title="Delete Table">
                    <FontAwesomeIcon icon={faTimes} />
                 </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default SlotTable;